import { avatarSeed, owlAvatarDataUri } from "@gryt/owl";
import { motion } from "motion/react";
import { useMemo, useState } from "react";

import { usePrefersReducedMotion } from "../../lib/usePrefersReducedMotion";
import { OwlDesigner } from "@gryt/ui";
import { DEMO_NAMES } from "../../data/demoNames";
import { useRotatingName } from "./useRotatingName";

import { inView, rise, stagger } from "./motion";
import styles from "./Bird.module.css";

/**
 * The client's own designer, in the page rather than behind a button. `followSeed` is the
 * prop this page needed: it opens on the owl the typed name draws and follows it.
 */
const AVATAR_DOCS = "https://docs.gryt.chat/docs/use/accounts";

/** Four names, four owls, drawn here rather than saved as images. */
const GALLERY = ["kasper", "nora", "tobias", "ida"];

export function Bird() {
  const reduced = usePrefersReducedMotion() ?? false;
  const [touched, setTouched] = useState(false);
  const [name, setName] = useRotatingName(DEMO_NAMES, touched || reduced, "sivert");

  const gallery = useMemo(
    () =>
      GALLERY.map((n) => ({
        name: n,
        uri: owlAvatarDataUri(avatarSeed(n) ?? n, { size: 160 }),
      })),
    [],
  );

  return (
    <section className={styles.section} id="bird">
      <motion.div className={styles.inner} variants={stagger(reduced)} {...inView}>
        {/* The label for the section, not a second headline. "Your own bird,
            if you want it" was a sentence in the slot every other section fills
            with one word, and the heading under it already says the same thing
            with more room to do it in. `Avatars` is also what this section is,
            since the upload half moved in here. */}
        <motion.p className={styles.eyebrow} variants={rise(reduced)}>
          Avatars
        </motion.p>
        <motion.h2 className={styles.title} variants={rise(reduced)}>
          Everybody gets an owl. Here's yours.
        </motion.h2>

        <motion.div
          className={styles.stage}
          variants={rise(reduced)}
          onPointerDown={() => setTouched(true)}
        >
          {/* Centred and given its own room. It used to sit as a small label
              and a box wedged between the heading and the designer, where it
              read as a caption rather than as the one thing on this section
              you are meant to type in. */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Type a name</span>
            <input
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 24))}
              onFocus={() => setTouched(true)}
              placeholder="your name"
              spellCheck={false}
              autoComplete="off"
            />
            <span className={styles.fieldHint}>
              Nothing gets sent anywhere. It's drawn in this tab.
            </span>
          </label>

          <div className={styles.designer}>
            <OwlDesigner
              nickname={name}
              followSeed
              saving={false}
              onUseInApp={(worn) => {
                window.location.href = "gryt://owl?worn=" + encodeURIComponent(worn);
              }}
              onSave={(png) => {
                // No account here to save to, so "use this owl" hands you the
                // file. A button that says it saved and did not is worse.
                const url = URL.createObjectURL(png);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${avatarSeed(name) ?? "owl"}-gryt.png`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            />
          </div>
        </motion.div>


        <motion.h3 className={styles.subTitle} variants={rise(reduced)}>
          Or use a picture you already have.
        </motion.h3>
        <motion.p className={styles.sub} variants={rise(reduced)}>
          A GIF stays a GIF, and so does an animated WebP. It keeps moving
          instead of getting flattened to the first frame, and it plays in the
          member list and next to every message you send. You don't have to pay
          for that. Up to 25&nbsp;MB, cropped square, stored at 256 pixels.
          Server icons work the same way.{" "}
          <a href={AVATAR_DOCS} target="_blank" rel="noreferrer">
            More on profiles
          </a>
          .
        </motion.p>

        <motion.ul className={styles.gallery} variants={rise(reduced)}>
          {gallery.map((g) => (
            <li className={styles.tile} key={g.name}>
              <img src={g.uri} alt={`The owl drawn for the name ${g.name}`} width={160} height={160} />
              <span>{g.name}</span>
            </li>
          ))}
          {/* The fifth tile is the upload, and it is drawn rather than
              photographed: a stand-in photo of a stranger would be the one
              thing in this row that is not real. */}
          <li className={styles.tile} data-upload="">
            <span className={styles.uploadArt} aria-hidden="true">
              <svg viewBox="0 0 64 64" width="64" height="64" fill="none">
                <rect x="6" y="12" width="52" height="40" rx="8" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="22" cy="26" r="5" stroke="currentColor" strokeWidth="2.5" />
                <path d="M9 45l14-13 10 9 8-7 14 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>yours</span>
          </li>
        </motion.ul>
      </motion.div>
    </section>
  );
}
